import {Component, OnInit} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {Subject, takeUntil} from "rxjs";
import {AdminService} from "../services/admin.service";
import {AuthenticationService} from "../../auth/services/authentication.service";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import Swal from "sweetalert2";
import {Course} from "../../shared/SharedEntities";
import {User} from "../../auth/models/User";
import {CourseDialogComponent} from "./course-dialog/course-dialog.component";

@Component({
  selector: 'app-courses',
  templateUrl: './courses.component.html',
  styleUrls: ['./courses.component.scss']
})
export class CoursesComponent implements OnInit {
  courses = new MatTableDataSource<Course>();
  currentUser!: User;
  displayedColumns: string[] = ['name', 'startDateOfApplication', 'endDateOfApplication', 'edit', 'remove'];
  sortDirection: 'desc' | 'asc' = 'desc';
  private allCourses: Course[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminService,
    private authenticationService: AuthenticationService,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
    this.authenticationService.currentUser.pipe(takeUntil(this.destroy$)).subscribe(x => {
      this.currentUser = x!;
    });
  }

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData() {
    this.adminService.getAllCourses().subscribe((result: Course[]) => {
      this.allCourses = result;
      this.applySort();
    }, (error: any) => {
      console.error(error);
    });
  }

  onSortDirectionChange(direction: 'desc' | 'asc'): void {
    this.sortDirection = direction;
    this.applySort();
  }

  private applySort(): void {
    const factor = this.sortDirection === 'desc' ? -1 : 1;
    const sorted = [...this.allCourses].sort((a, b) =>
      factor * (new Date(a.startDateOfApplication).getTime() - new Date(b.startDateOfApplication).getTime())
    );
    this.courses = new MatTableDataSource(sorted);
  }

  openCreateCourseDialog(): void {
    const dialogRef = this.dialog.open(CourseDialogComponent, {
      width: '450px',
      data: {course: null}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  openEditCourseDialog(course: Course): void {
    const dialogRef = this.dialog.open(CourseDialogComponent, {
      width: '450px',
      data: {course}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  deleteCourse(id: string): void {
    this.translate.get([
      'Are you sure?',
      'You won\'t be able to revert this!',
      'Yes, delete it!',
      'No, cancel!',
      'Deleted',
      'The course has been deleted.',
      'Error',
      'An error occurred while deleting the course.'
    ]).subscribe(translations => {
      Swal.fire({
        title: translations['Are you sure?'],
        text: translations['You won\'t be able to revert this!'],
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: translations['Yes, delete it!'],
        cancelButtonText: translations['No, cancel!']
      }).then((result: any) => {
        if (result.isConfirmed) {
          this.adminService.deleteCourse(id).subscribe(() => {
            Swal.fire(translations['Deleted'], translations['The course has been deleted.'], 'success');
            this.loadData();
          }, (error) => {
            Swal.fire(translations['Error'], translations['An error occurred while deleting the course.'], 'error');
          });
        }
      });
    });
  }
}
